import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Shield, Zap, Play, Square, Plus, Loader2, CheckCircle, XCircle, DollarSign, FileText, Upload, FileDown, Landmark, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const QLD_CYCLONE_POSTCODES = ["4870","4871","4868","4810","4811","4812","4814","4815","4816","4817","4818","4819","4740","4741"];
const NSW_FLOOD_POSTCODES = ["2480","2481","2482","2340","2341","2330","2325","2321","2320"];
const VIC_STORM_POSTCODES = ["3844","3840","3842","3280","3282","3500","3502"];

function SalesOnePagerCard({ api }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const resp = await api.get('/cat/kit/one-pager.pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'CAT_Pilot_One_Pager.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
    setBusy(false);
  };
  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-4" data-testid="cat-sales-kit-card">
      <div className="flex items-center gap-2 mb-2">
        <Briefcase className="w-3.5 h-3.5" style={{ color: '#1A4C7C' }} />
        <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0B1E3D' }}>Sales Kit — CAT Pilot One-Pager</h3>
      </div>
      <p className="text-[11px] mb-3" style={{ color: '#5C6B7A' }}>
        Printable page for the pitch: what the insurer sees, what the pilot needs from them, and pricing (on application).
        Hand it to a claims GM after the demo.
      </p>
      <Button size="sm" variant="outline" onClick={download} disabled={busy} className="rounded-sm text-xs" data-testid="cat-onepager-download-btn">
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1.5" />}
        Download one-pager (PDF)
      </Button>
    </Card>
  );
}

function PolicyImportCard({ api }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => { api.get('/cat/policy-import/status').then(r => setStatus(r.data)).catch(() => {}); }, [api, result]);

  const runImport = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/cat/policy-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data); setFile(null);
    } catch (e) { setResult({ error: e?.response?.data?.detail || 'Import failed' }); }
    setBusy(false);
  };

  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-5" data-testid="policy-import-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0B1E3D' }}>Client Policy Extract — coverage verification</h3>
        {status?.imported_policies > 0 && <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }} data-testid="imported-policies-count">{status.imported_policies.toLocaleString()} imported policies on file</span>}
      </div>
      <p className="text-xs mb-3" style={{ color: '#5C6B7A' }}>Upload the client's policy book (CSV/XLSX: policy number, insured, postcode, excess, sum insured…) so CAT lodgement verifies coverage and excess against <b>their</b> book. Imported policies carry no premium into platform figures and never overwrite platform-written policies.</p>
      <div className="flex flex-wrap items-center gap-3">
        <input type="file" accept=".csv,.xlsx" data-testid="policy-import-file"
          onChange={e => setFile(e.target.files?.[0] || null)}
          className="text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded-sm file:border-0 file:text-xs file:font-medium file:bg-[#0B1E3D] file:text-white" />
        <Button size="sm" onClick={runImport} disabled={!file || busy} className="rounded-sm text-xs" style={{ background: '#0B1E3D' }} data-testid="policy-import-btn">
          {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />} Import extract
        </Button>
      </div>
      {result && (
        <div className="mt-3 p-3 rounded-sm text-xs" style={{ background: result.error ? 'rgba(226,75,74,0.06)' : 'rgba(29,158,117,0.06)' }} data-testid="policy-import-result">
          {result.error ? <span style={{ color: '#E24B4A' }}>{result.error}</span> : (
            <span style={{ color: '#0B1E3D' }}><b>{result.imported}</b> imported · <b>{result.updated}</b> updated · <b>{result.skipped}</b> skipped of {result.rows} rows{(result.notes || []).length > 0 && <span className="block mt-1" style={{ color: '#5C6B7A' }}>{result.notes.slice(0, 3).join(' · ')}</span>}</span>
          )}
        </div>
      )}
    </Card>
  );
}

function PaymentInstructionsCard({ api, catEventId }) {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editCfg, setEditCfg] = useState(false);
  const [form, setForm] = useState({ originator_name: '', bank_code: '', bsb: '', account_number: '', apca_id: '', remitter: '', description: 'CLAIMS PAY' });
  const [payeeDrafts, setPayeeDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/claims/payment-rail/config').then(r => { setCfg(r.data); if (r.data.originator_name) setForm(f => ({ ...f, ...r.data })); }).catch(() => {});
    api.get(`/claims/payment-run/preview${catEventId ? `?cat_event_id=${catEventId}` : ''}`).then(r => setPreview(r.data)).catch(() => {});
  }, [api, catEventId]);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    setBusy(true);
    try { await api.put('/claims/payment-rail/config', form); setEditCfg(false); load(); }
    catch (e) { setPreview(p => ({ ...p, cfg_error: e?.response?.data?.detail })); }
    setBusy(false);
  };

  const savePayee = async (claimId) => {
    const d = payeeDrafts[claimId] || {};
    try {
      const r = await api.post(`/claims/${claimId}/payee`, { payee_bsb: d.bsb || '', payee_account: d.account || '', payee_name: d.name || '' });
      if (r.data?.sanctions?.held) toast.warning(r.data.message); else toast.success('Payee recorded — screened clean');
      load();
    }
    catch (e) { setPreview(p => ({ ...p, cfg_error: e?.response?.data?.detail })); }
  };

  const clearHold = async (claimId) => {
    const reason = window.prompt('Written basis for clearing the sanctions hold (at least 12 characters):');
    if (!reason) return;
    try { await api.post(`/claims/${claimId}/payment-hold/clear`, { reason }); toast.success('Sanctions hold cleared'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Hold not cleared'); }
  };

  const exportAba = async () => {
    setBusy(true);
    try {
      const resp = await api.post('/claims/payment-run/export-aba', { cat_event_id: catEventId || null }, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'claim-payments.aba'; a.click();
      URL.revokeObjectURL(url);
      load();
    } catch (e) {
      let detail = 'Export failed';
      try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* keep default */ }
      setPreview(p => ({ ...p, cfg_error: detail }));
    }
    setBusy(false);
  };

  const configured = cfg?.configured;
  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-5" data-testid="payment-instructions-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4" style={{ color: '#0B1E3D' }} />
          <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0B1E3D' }}>Payment Instructions (ABA){catEventId ? ' — this CAT event' : ''}</h3>
        </div>
        <div className="flex items-center gap-2">
          {configured
            ? <span className="text-[10px] px-2 py-0.5 rounded-sm" style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }} data-testid="rail-configured-chip">rail configured · {cfg.originator_name}</span>
            : <span className="text-[10px] px-2 py-0.5 rounded-sm" style={{ background: 'rgba(226,75,74,0.1)', color: '#E24B4A' }} data-testid="rail-unconfigured-chip">bank details required</span>}
          <button onClick={() => setEditCfg(!editCfg)} className="text-[10px] underline" style={{ color: '#5C6B7A' }} data-testid="rail-config-toggle">{editCfg ? 'close' : 'configure'}</button>
        </div>
      </div>
      <p className="text-xs mb-3" style={{ color: '#5C6B7A' }}>Generates a bank-upload <b>.aba</b> direct-entry file for settled claims — the client's bank executes it. We produce the instruction; money never moves through the platform.</p>

      {editCfg && (
        <div className="mb-3 p-3 rounded-sm space-y-2" style={{ background: '#F5F7FA' }} data-testid="rail-config-form">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[['originator_name', 'Account title (originator)'], ['bank_code', 'Bank code (e.g. CBA)'], ['bsb', 'BSB'], ['account_number', 'Account number'], ['apca_id', 'APCA user ID (6 digits)'], ['remitter', 'Remitter name (on their statement)']].map(([k, ph]) => (
              <Input key={k} value={form[k] || ''} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} placeholder={ph} className="rounded-sm text-xs h-8" data-testid={`rail-${k}-input`} />
            ))}
          </div>
          <Button size="sm" onClick={saveCfg} disabled={busy} className="rounded-sm text-xs" style={{ background: '#0B1E3D' }} data-testid="rail-config-save">Save rail config</Button>
        </div>
      )}

      {preview && (
        <>
          <div className="flex flex-wrap gap-4 text-xs mb-3" data-testid="payment-preview-stats">
            <span>Ready for ABA: <b style={{ color: '#1D9E75' }}>{preview.ready_aba}</b> (${(preview.ready_aba_total || 0).toLocaleString()})</span>
            <span>Missing bank details: <b style={{ color: '#D96000' }}>{preview.missing_payee?.length || 0}</b></span>
            <span>Held on discharge: <b style={{ color: '#5C6B7A' }}>{preview.held}</b></span>
            <span data-testid="payment-preview-sanctions-held">Held on sanctions: <b style={{ color: preview.sanctions_held ? '#E24B4A' : '#5C6B7A' }}>{preview.sanctions_held || 0}</b>{preview.sanctions_held ? ` ($${(preview.sanctions_held_total || 0).toLocaleString()})` : ''}</span>
          </div>
          {(preview.sanctions_held_claims || []).slice(0, 6).map(c => (
            <div key={c.claim_id} className="flex flex-wrap items-center gap-2 py-1.5 border-t border-[rgba(136,150,168,0.1)]" data-testid={`sanctions-held-${c.claim_id}`}>
              <span className="text-xs w-40 truncate" style={{ color: '#0B1E3D' }}>{c.claim_reference} · {c.business_name}</span>
              <span className="text-xs font-mono" style={{ color: '#5C6B7A' }}>${(c.paid_amount || 0).toLocaleString()}</span>
              <span className="text-[11px] flex-1 min-w-[200px]" style={{ color: '#E24B4A' }}>{c.payment_hold?.text}</span>
              <Button size="sm" variant="outline" onClick={() => clearHold(c.claim_id)} className="rounded-sm text-[10px] h-7" data-testid={`clear-hold-${c.claim_id}`}>Clear with reason</Button>
            </div>
          ))}
          {(preview.missing_payee || []).slice(0, 6).map(c => (
            <div key={c.claim_id} className="flex flex-wrap items-center gap-2 py-1.5 border-t border-[rgba(136,150,168,0.1)]" data-testid={`missing-payee-${c.claim_id}`}>
              <span className="text-xs w-40 truncate" style={{ color: '#0B1E3D' }}>{c.claim_reference} · {c.business_name}</span>
              <span className="text-xs font-mono" style={{ color: '#5C6B7A' }}>${(c.paid_amount || 0).toLocaleString()}</span>
              <Input placeholder="BSB" className="rounded-sm text-xs h-7 w-24" data-testid={`payee-bsb-${c.claim_id}`}
                onChange={e => setPayeeDrafts(p => ({ ...p, [c.claim_id]: { ...p[c.claim_id], bsb: e.target.value } }))} />
              <Input placeholder="Account" className="rounded-sm text-xs h-7 w-28" data-testid={`payee-account-${c.claim_id}`}
                onChange={e => setPayeeDrafts(p => ({ ...p, [c.claim_id]: { ...p[c.claim_id], account: e.target.value } }))} />
              <Button size="sm" variant="outline" onClick={() => savePayee(c.claim_id)} className="rounded-sm text-[10px] h-7" data-testid={`payee-save-${c.claim_id}`}>Save payee</Button>
            </div>
          ))}
          <div className="mt-3 flex items-center gap-3">
            <Button size="sm" onClick={exportAba} disabled={busy || !configured || !preview.ready_aba} className="rounded-sm text-xs" style={{ background: '#1D9E75' }} data-testid="export-aba-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileDown className="w-3 h-3 mr-1" />} Generate ABA file ({preview.ready_aba})
            </Button>
            {preview.cfg_error && <span className="text-xs" style={{ color: '#E24B4A' }} data-testid="payment-error">{preview.cfg_error}</span>}
          </div>
        </>
      )}
    </Card>
  );
}

function PilotBrandCard({ api, brand, onChange }) {
  const [name, setName] = useState(brand?.insurer_name || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { if (brand?.insurer_name) setName(brand.insurer_name); }, [brand?.insurer_name]);

  const save = async () => {
    setBusy(true); setErr('');
    try { await api.put('/cat/brand', { insurer_name: name }); onChange(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
    setBusy(false);
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      await api.post('/cat/brand/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange();
    } catch (e) { setErr(e?.response?.data?.detail || 'Logo upload failed'); }
    setBusy(false);
  };
  const clear = async () => { setBusy(true); try { await api.delete('/cat/brand'); setName(''); onChange(); } catch { /* noop */ } setBusy(false); };

  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-5" data-testid="pilot-brand-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0B1E3D' }}>Pilot Branding — white label</h3>
        {brand?.configured && <span className="text-[10px] px-2 py-0.5 rounded-sm" style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }} data-testid="brand-configured-chip">branded: {brand.insurer_name}</span>}
      </div>
      <p className="text-xs mb-3" style={{ color: '#5C6B7A' }}>The insurer's own name and logo carry across the FNOL screen, the CAT demo and the situation report — underAIter.com stays in the prepared-by line.</p>
      <div className="flex flex-wrap items-center gap-2">
        {brand?.logo_data && <img src={brand.logo_data} alt="pilot logo" className="h-9 w-auto rounded-sm px-1" style={{ background: '#F5F7FA' }} data-testid="brand-logo-preview" />}
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Insurer name — e.g. Southern Cross General Insurance" className="rounded-sm text-xs h-8 w-72" data-testid="brand-name-input" />
        <Button size="sm" onClick={save} disabled={busy || !name.trim()} className="rounded-sm text-xs" style={{ background: '#0B1E3D' }} data-testid="brand-save-btn">Save name</Button>
        <input type="file" accept="image/png,image/jpeg" data-testid="brand-logo-input" onChange={e => uploadLogo(e.target.files?.[0])}
          className="text-xs file:mr-2 file:px-3 file:py-1.5 file:rounded-sm file:border-0 file:text-xs file:font-medium file:bg-[#00B4D8] file:text-[#0B1E3D]" />
        {brand?.configured && <button onClick={clear} className="text-[11px] underline" style={{ color: '#E24B4A' }} data-testid="brand-clear-btn">remove branding</button>}
      </div>
      {err && <p className="text-xs mt-2" style={{ color: '#E24B4A' }} data-testid="brand-error">{err}</p>}
    </Card>
  );
}

export default function CATModePage() {
  const api = useApi();
  const { user } = useAuth();
  const [catStatus, setCatStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [lodging, setLodging] = useState(false);
  const [showFNOL, setShowFNOL] = useState(false);
  const [fnolForm, setFnolForm] = useState({ business_name: '', policy_number: '', claim_type: 'property', description: '', estimated_quantum: '' });
  const [eventForm, setEventForm] = useState({ event_name: '', postcodes: '', elevated_authority: 25000 });
  const [agentHealth, setAgentHealth] = useState(null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [brand, setBrand] = useState(null);

  const loadBrand = useCallback(() => {
    api.get('/cat/brand').then(r => setBrand(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { loadBrand(); }, [loadBrand]);

  const loadStatus = useCallback(async () => {
    try {
      const [cat, health, queue] = await Promise.all([
        api.get('/cat/status'),
        api.get('/agent-runtime/health'),
        api.get('/agent-runtime/queue'),
      ]);
      setCatStatus(cat.data);
      setAgentHealth(health.data);
      setQueueStatus(queue.data);
    } catch (err) { console.error("API error:", err?.message); }
    setLoading(false);
  }, [api]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Poll while CAT active
  useEffect(() => {
    if (catStatus?.status !== 'active') return;
    const timer = setInterval(loadStatus, 5000);
    return () => clearInterval(timer);
  }, [catStatus?.status, loadStatus]);

  const activateCAT = async (preset) => {
    setActivating(true);
    try {
      const data = preset || { event_name: eventForm.event_name, affected_postcodes: eventForm.postcodes.split(',').map(p => p.trim()), elevated_authority: eventForm.elevated_authority };
      await api.post('/cat/activate', data);
      await loadStatus();
    } catch (err) { console.error("API error:", err?.message); }
    setActivating(false);
  };

  const deactivateCAT = async () => {
    await api.post('/cat/deactivate');
    await loadStatus();
  };

  const lodgeFNOL = async () => {
    setLodging(true);
    try {
      await api.post('/cat/fnol', { ...fnolForm, estimated_quantum: parseFloat(fnolForm.estimated_quantum) || 0 });
      setFnolForm({ business_name: '', policy_number: '', claim_type: 'property', description: '', estimated_quantum: '' });
      await loadStatus();
    } catch (err) { console.error("API error:", err?.message); }
    setLodging(false);
  };

  const isAdmin = ['platform_owner', 'mga_admin'].includes(user?.role);
  const isActive = catStatus?.status === 'active';
  const isRecovery = catStatus?.status === 'recovery';

  if (loading) return <Skeleton className="h-96 rounded-sm" />;

  return (
    <div className="space-y-6" data-testid="cat-mode-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            {brand?.logo_data && <img src={brand.logo_data} alt={brand.insurer_name} className="h-9 w-auto" data-testid="cat-brand-logo" />}
            <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#0B1E3D', fontFamily: "Georgia, serif" }} data-testid="cat-mode-title">{brand?.insurer_name ? `${brand.insurer_name} — CAT Mode` : 'CAT Mode'}</h1>
            {isActive && <span className="px-3 py-1 rounded-sm text-xs font-bold text-white animate-pulse" style={{ background: '#E24B4A' }}>ACTIVE</span>}
            {isRecovery && <span className="px-3 py-1 rounded-sm text-xs font-bold text-white" style={{ background: '#D96000' }}>RECOVERY</span>}
            {!isActive && !isRecovery && <span className="px-3 py-1 rounded-sm text-xs font-medium" style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }}>NORMAL</span>}
          </div>
          <p className="text-sm mt-1" style={{ color: '#5C6B7A' }}>{brand?.insurer_name ? `${brand.insurer_name} catastrophe claims — powered by underAIter.com` : 'Catastrophe event claims processing — surge capacity'}</p>
        </div>
        {isActive && isAdmin && (
          <Button onClick={deactivateCAT} variant="outline" className="rounded-sm text-xs border-[#D96000] text-[#D96000]">
            <Square className="w-3 h-3 mr-1" /> Deactivate → Recovery Mode
          </Button>
        )}
      </div>

      {/* Active CAT Event */}
      {(isActive || isRecovery) && catStatus && (
        <>
          <Card className="rounded-sm border-l-4 bg-white p-5" style={{ borderLeftColor: isActive ? '#E24B4A' : '#D96000' }}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold" style={{ color: '#0B1E3D' }}>{catStatus.event_name}</h2>
                <p className="text-xs" style={{ color: '#5C6B7A' }}>Activated: {new Date(catStatus.activated_at).toLocaleString()} | Postcodes: {catStatus.affected_postcodes?.length}</p>
              </div>
              <div className="text-right">
                <p className="text-xs" style={{ color: '#5C6B7A' }}>Auto-settlement elevated to</p>
                <p className="text-xl font-bold font-mono" style={{ color: '#D96000' }}>${catStatus.elevated_authority?.toLocaleString()}</p>
              </div>
            </div>
          </Card>

          {/* Live Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'FNOLs Received', value: catStatus.stats?.fnol_count || catStatus.live_stats?.total_claims || 0, icon: FileText, color: '#1B4A9A' },
              { label: 'Open Claims', value: catStatus.live_stats?.open || 0, icon: AlertTriangle, color: '#E24B4A' },
              { label: 'Auto-Settled', value: catStatus.stats?.auto_settled || catStatus.live_stats?.settled || 0, icon: CheckCircle, color: '#1D9E75' },
              { label: 'Total Reserves', value: `$${((catStatus.live_stats?.total_reserves || catStatus.stats?.total_reserves || 0) / 1000).toFixed(0)}K`, icon: DollarSign, color: '#D96000' },
              { label: 'Entities Held', value: catStatus.entities_currently_held || catStatus.entities_held || 0, icon: Shield, color: '#5C6B7A' },
            ].map(kpi => (
              <Card key={kpi.label} className="p-3 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
                <div className="flex items-center gap-2 mb-1">
                  <kpi.icon className="w-3.5 h-3.5" style={{ color: kpi.color }} />
                  <span className="text-[9px] uppercase tracking-wider" style={{ color: '#5C6B7A' }}>{kpi.label}</span>
                </div>
                <p className="text-lg font-bold font-mono" style={{ color: '#0B1E3D' }}>{kpi.value}</p>
              </Card>
            ))}
          </div>

          {/* CAT FNOL Form */}
          {isActive && (
            <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {brand?.logo_data && <img src={brand.logo_data} alt={brand.insurer_name} className="h-6 w-auto" data-testid="fnol-brand-logo" />}
                  <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#0B1E3D' }} data-testid="fnol-form-title">Lodge CAT FNOL (Simplified){brand?.insurer_name ? ` — ${brand.insurer_name}` : ''}</h3>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-sm" style={{ background: 'rgba(217,96,0,0.1)', color: '#D96000' }}>5 fields only</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs" style={{ color: '#5C6B7A' }}>Business Name</Label>
                  <Input value={fnolForm.business_name} onChange={e => setFnolForm(p => ({...p, business_name: e.target.value}))} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs" style={{ color: '#5C6B7A' }}>Policy Number</Label>
                  <Input value={fnolForm.policy_number} onChange={e => setFnolForm(p => ({...p, policy_number: e.target.value}))} className="mt-1 rounded-sm" /></div>
                <div><Label className="text-xs" style={{ color: '#5C6B7A' }}>Estimated Amount ($)</Label>
                  <Input type="number" value={fnolForm.estimated_quantum} onChange={e => setFnolForm(p => ({...p, estimated_quantum: e.target.value}))} className="mt-1 rounded-sm" /></div>
                <div className="md:col-span-2"><Label className="text-xs" style={{ color: '#5C6B7A' }}>Description</Label>
                  <Input value={fnolForm.description} onChange={e => setFnolForm(p => ({...p, description: e.target.value}))} className="mt-1 rounded-sm" placeholder="Brief description of damage/loss" /></div>
                <div className="flex items-end">
                  <Button onClick={lodgeFNOL} disabled={lodging} className="w-full rounded-sm" style={{ background: '#E24B4A' }}>
                    {lodging ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
                    Lodge CAT FNOL
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Affected Postcodes */}
          <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#0B1E3D' }}>Affected Postcodes — New Business HELD (enforced at quoting)</h3>
            <div className="flex flex-wrap gap-1">
              {(catStatus.affected_postcodes || []).map(pc => (
                <span key={pc} className="text-xs px-2 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(226,75,74,0.1)', color: '#E24B4A' }}>{pc}</span>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Activation Panel */}
      {!isActive && !isRecovery && isAdmin && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-5 space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#0B1E3D' }}>Activate CAT Mode</h3>
          
          {/* Presets */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { name: 'QLD Cyclone', postcodes: QLD_CYCLONE_POSTCODES, icon: '🌀', desc: 'Cairns, Townsville, Mackay' },
              { name: 'NSW Flood', postcodes: NSW_FLOOD_POSTCODES, icon: '🌊', desc: 'Northern Rivers, Hunter Valley' },
              { name: 'VIC Storm', postcodes: VIC_STORM_POSTCODES, icon: '⛈️', desc: 'Gippsland, Western Districts' },
            ].map(preset => (
              <button key={preset.name} onClick={() => activateCAT({ event_name: preset.name, affected_postcodes: preset.postcodes, elevated_authority: 25000 })}
                disabled={activating} className="p-4 rounded-sm border text-left hover:border-[#E24B4A] transition-all" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
                <span className="text-2xl">{preset.icon}</span>
                <p className="text-sm font-bold mt-2" style={{ color: '#0B1E3D' }}>{preset.name}</p>
                <p className="text-[10px]" style={{ color: '#5C6B7A' }}>{preset.postcodes.length} postcodes | {preset.desc}</p>
              </button>
            ))}
          </div>

          {/* Custom */}
          <div className="pt-3 border-t border-[rgba(136,150,168,0.1)]">
            <p className="text-xs font-semibold mb-2" style={{ color: '#5C6B7A' }}>Or custom event:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input placeholder="Event name" value={eventForm.event_name} onChange={e => setEventForm(p => ({...p, event_name: e.target.value}))} className="rounded-sm" />
              <Input placeholder="Postcodes (comma-separated)" value={eventForm.postcodes} onChange={e => setEventForm(p => ({...p, postcodes: e.target.value}))} className="rounded-sm" />
              <Button onClick={() => activateCAT()} disabled={activating || !eventForm.event_name} className="rounded-sm" style={{ background: '#E24B4A' }}>
                {activating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
                Activate CAT Mode
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Agent Health */}
      {agentHealth && Object.keys(agentHealth.agents || {}).length > 0 && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#0B1E3D' }}>Agent Health — Live</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(agentHealth.agents).map(([name, h]) => (
              <div key={name} className="p-2 rounded-sm" style={{ background: '#F5F7FA' }}>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ background: h.health === 'healthy' ? '#1D9E75' : h.health === 'degraded' ? '#D96000' : '#E24B4A' }} />
                  <span className="text-[10px] font-bold" style={{ color: '#0B1E3D' }}>{name}</span>
                </div>
                <p className="text-[9px] mt-0.5" style={{ color: '#5C6B7A' }}>{h.tasks_processed} tasks | {h.avg_duration_ms}ms avg | {h.error_rate_pct}% errors</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Pilot kit: white-label branding + policy extract import + ABA payment instructions (admin) */}
      {isAdmin && <SalesOnePagerCard api={api} />}
      {isAdmin && <PilotBrandCard api={api} brand={brand} onChange={loadBrand} />}
      {isAdmin && <PolicyImportCard api={api} />}
      {isAdmin && <PaymentInstructionsCard api={api} catEventId={isActive || isRecovery ? catStatus?.event_id : null} />}

      {/* Queue Status */}
      {queueStatus && queueStatus.total_tasks > 0 && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white p-4">
          <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#0B1E3D' }}>Task Queue</h3>
          <div className="flex gap-4 text-xs">
            <span>Pending: <strong>{queueStatus.pending}</strong></span>
            <span>Processing: <strong>{queueStatus.processing}</strong></span>
            <span>Complete: <strong style={{ color: '#1D9E75' }}>{queueStatus.complete}</strong></span>
            <span>Failed: <strong style={{ color: '#E24B4A' }}>{queueStatus.failed}</strong></span>
          </div>
        </Card>
      )}
    </div>
  );
}
